import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftCircleIcon,
} from '@heroicons/react/24/outline';
import { ArrowBigLeftDash, ArrowBigRightDash } from 'lucide-react';

import { Input } from '@material-tailwind/react';

import { isMobile } from 'react-device-detect';
import { v4 as uuidv4 } from 'uuid';
import { mintCheck } from '../util/api';
import { useNavigate } from 'react-router-dom';
import apImg from '../assets/logos/ap.png';
import decryptImg from '../assets/logos/decrypt.png';
import biImg from '../assets/logos/bi.png';
import binanceImg from '../assets/logos/binance.png';
import marketwatchImg from '../assets/logos/marketwatch.png';
import youtubeImg from '../assets/logos/youtube.png';
import morningstarImg from '../assets/logos/morningstart.png';
import cointelegraphImg from '../assets/logos/cointelegraph.png';
import logo from '../assets/imgs/logo.webp';
import img1 from '../assets/news/1.webp';
import img2 from '../assets/news/2.webp';
import img3 from '../assets/news/3.webp';
import img4 from '../assets/news/4.webp';
import img5 from '../assets/news/5.webp';
import img6 from '../assets/news/6.webp';
import img7 from '../assets/news/7.webp';
import img8 from '../assets/news/8.webp';
import img9 from '../assets/news/9.webp';
import img10 from '../assets/news/10.webp';
import img11 from '../assets/news/11.webp';
import img12 from '../assets/news/12.webp';

export default function News({ isMaintenance }) {
  const navigate = useNavigate();

  if (isMaintenance) {
    navigate('/maintenance');
  }

  const [currentSlide, setCurrentSlide] = useState(0);

  // Total number of logos
  const totalLogos = 8;
  const maxSlide = isMobile ? totalLogos + 2 : totalLogos - 4;

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => {
      if (prev === 0) {
        return maxSlide;
      }
      return prev - 1;
    });
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => {
      if (prev === maxSlide) {
        return 0;
      }
      return prev + 1;
    });
  };

  return (
    <div className='flex flex-col z-[9999] bg-background w-full'>
      <div className='md:hidden flex flex-col gap-5 items-center px-2 w-full justify-center mt-4'>
        <img src={logo} className='h-[60px]' onClick={() => navigate('/')} />
        <div className='text-red-900 text-2xl font-bold text-center mt-3'>
          NEWS
        </div>
      </div>
      <div className='flex flex-col'>
        <div className='text-white text-lg font-bold text-center mt-10'>
          AS FEATURED IN
        </div>
        <div className='relative w-full flex items-center justify-center gap-3 mt-5'>
          <ArrowBigLeftDash
            className='h-6 w-6 cursor-pointer text-white'
            onClick={handlePrevSlide}
          />
          <div className='flex overflow-hidden'>
            <div
              className='flex gap-8 transition-transform duration-500 ease-in-out max-w-[300px] md:max-w-[500px]'
              style={{ transform: `translateX(-${currentSlide * 25}%)` }}
            >
              <img
                src={apImg}
                className='h-[30px] cursor-poiter'
                onClick={() =>
                  window.open(
                    'https://apnews.com/press-release/globenewswire-mobile/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection-c3906c82d303c29889dbe027a9309d67',
                    '_blank'
                  )
                }
              />
              <img
                src={decryptImg}
                className='h-[30px] cursor-pointer'
                onClick={() =>
                  window.open(
                    'https://decrypt.co/240924/mr-black-to-unveil-groundbreaking-21000-piece-ordinal-collection-on-bitcoin',
                    '_blank'
                  )
                }
              />
              <img
                src={biImg}
                className='h-[30px] cursor-pointer'
                onClick={() =>
                  window.open(
                    'https://markets.businessinsider.com/news/stocks/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection-1034863191',
                    '_blank'
                  )
                }
              />
              <img
                src={binanceImg}
                className='h-[30px] cursor-pointer'
                onClick={() =>
                  window.open(
                    'https://www.binance.com/en/square/post/26354530425794',
                    '_blank'
                  )
                }
              />
              <img
                src={marketwatchImg}
                className='h-[30px] cursor-pointer'
                onClick={() =>
                  window.open(
                    'https://www.marketwatch.com/press-release/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection-31b2d9da?mod=search_headline',
                    '_blank'
                  )
                }
              />
              <img
                src={youtubeImg}
                className='h-[30px] cursor-pointer'
                onClick={() =>
                  window.open(
                    'https://www.youtube.com/watch?v=_vAdfCKnRO4',
                    '_blank'
                  )
                }
              />
              <img
                src={morningstarImg}
                className='h-[30px] cursor-pointer'
                onClick={() =>
                  window.open(
                    'https://www.fshnmagazine.com/shades-of-opportunites-in-mr-black-business',
                    '_blank'
                  )
                }
              />
              <img
                src={cointelegraphImg}
                className='h-[30px] cursor-pointer'
                onClick={() =>
                  window.open(
                    'https://cointelegraph.com/press-releases/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection',
                    '_blank'
                  )
                }
              />
            </div>
          </div>
          <ArrowBigRightDash
            className='h-6 w-6 cursor-pointer text-white'
            onClick={handleNextSlide}
          />
        </div>
      </div>
      <div className='flex flex-col items-center justify-center w-full'>
        <div className='grid md:grid-cols-2 grid-cols-1 gap-10 mt-10 max-w-[900px] p-3'>
          <a
            href='https://markets.businessinsider.com/news/stocks/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection-1034863191'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img2}
              className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://www.marketwatch.com/press-release/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection-31b2d9da?mod=search_headline'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img3}
              className='w-full h-full object-cover transform rotate-[10deg] hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://apnews.com/press-release/globenewswire-mobile/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection-c3906c82d303c29889dbe027a9309d67'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img4}
              className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://cointelegraph.com/press-releases/the-eyes-are-always-watching-launches-free-21k-bitcoin-ordinals-collection'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img1}
              className='w-full h-full object-cover rotate-[10deg] hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://magazine.atavist.com/2025/all-that-glitters-jona-rechnitz-lawsuit-jadelle-jewelry-coba-eth'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img5}
              className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://www.fshnmagazine.com/shades-of-opportunites-in-mr-black-business/'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img6}
              className='w-full h-full object-cover transform rotate-[10deg] hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://www.law.com/dailybusinessreview/2025/01/31/crypto-artist-sues-over-alleged-fraud-in-5-million-elon-musk-scheme/?slreturn=20250720193922'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img7}
              className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://www.youtube.com/watch?v=_vAdfCKnRO4'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img8}
              className='w-full h-full object-cover transform rotate-[10deg] hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://resident.com/business-and-finance/2024/07/25/groundbreaking-work-from-renowned-artist-mr-black-to-be-unveiled-on-x-with-21000-piece-ordinal-collection-on-bitcoin'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img9}
              className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://decrypt.co/240924/mr-black-to-unveil-groundbreaking-21000-piece-ordinal-collection-on-bitcoin'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img10}
              className='w-full h-full object-cover transform rotate-[10deg] hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://t2conline.com/mr-black-is-back-and-this-time-in-times-square/'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img11}
              className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
          <a
            href='https://www.binance.com/en/square/post/26354530425794'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src={img12}
              className='w-full h-full object-cover transform rotate-[10deg] hover:scale-105 transition-transform duration-200 cursor-pointer'
            />
          </a>
        </div>
      </div>
    </div>
  );
}
